import React, { useEffect, useMemo, useState } from 'react';
import CusPageCardContainer from '../../../components/shared/CusPageCardContainer';
import { Pagination, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import { useTranslation } from 'react-i18next';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import { useParams } from 'react-router';
import { API_USERS } from '../../../api/users';
import CusButton from '../../../components/material-ui/CusButton';
import LogsTable from './LogsTable';
import { ENUM_FILTER_JOURNAL } from '../../../enums/enums';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import { useDebounceEffect } from '../../../hooks/useDebounceEffect';
import { searchWithoutRegister } from '../../../utils';
import useColor from '../../../hooks/site/useColor';
import { useAppSelector } from '../../../store';

export default function UsersLogs() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { green, red, orange, grey } = useColor();
  const lang = useAppSelector((state) => state.site.lang);

  const [eventsOptions, setEventsOptions] = useState([]);
  const postOptions = [100, 200, 500, 1000, 2000].map((el) => ({ label: el, value: el }));

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [selectEvents, setSelectEvents] = useState([]);
  const [selectRecord, setSelectRecord] = useState(postOptions[2].value);
  const [count, setCount] = useState(0);
  const [curPage, setCurPage] = useState(1);
  const [allLogs, setAllLogs] = useState([]);
  const [logs, setLogs] = useState(allLogs);
  const [countRecord, setCountRecord] = useState(selectRecord);
  const [eventFiltered, setEventFiltered] = useState('');
  const [user, setUser] = useState({});

  const actionColor = [
    { action: ['balanceRefill'], color: green['200'] },
    { action: ['userPayout'], color: red['200'] },
    { action: ['enrollWin'], color: orange['200'] },
    { action: ['startGame', 'gameSpin'], color: grey[200] }
  ];

  const statusByEvent = {
    351: 'balanceRefill',
    316: 'balanceRefill',
    401: 'userPayout',
    101: 'startGame',
    151: 'startGame',
    102: 'gameSpin',
    153: 'gameSpin',
    103: 'enrollWin',
    157: 'enrollWin',
    116: 'enrollJackpot',
    500: 'bonus',
    60: 'botMessage'
  };

  const colorsOptions = actionColor.reduce((acc, item) => {
    item.action.forEach((action) => {
      acc[action] = item.color;
    });
    return acc;
  }, {});

  const getColorRow = (statusId) => {
    const status = statusByEvent[statusId];
    return colorsOptions[status];
  };

  const getJournalList = (pageSize) => {
    API_USERS.journal
      .list({
        userId: +userId,
        dateFrom: startDate,
        dateTo: endDate,
        filter: selectEvents,
        pageNo: curPage,
        lang,
        pageSize
      })
      .then((e) => {
        const data = e.data?.data;
        const list = data?.list || [];
        const page = data?.page || {};

        setUser(data.user);
        setAllLogs(list);
        makeEventFiltered(list);
        setCount(page.count || 0);
        setCurPage(page.no || 1);
        if (page.size) {
          setCountRecord(page.size);
          setSelectRecord(page.size);
        }
      })
      .catch(console.log);
  };

  const makeEventFiltered = (allLogs) => {
    if (!allLogs.length) return;
    setLogs(allLogs.filter((el) => searchWithoutRegister(el[2], eventFiltered)));
  };

  useDebounceEffect(() => {
    makeEventFiltered(allLogs);
  }, [eventFiltered]);

  useEffect(() => {
    API_USERS.journal.getFilter().then((e) => {
      const list = e.data?.list;

      setEventsOptions(list?.map((el) => ({ label: ENUM_FILTER_JOURNAL[el], value: el })));
    });
  }, []);

  useEffect(() => {
    getJournalList(countRecord);
  }, [curPage, lang]);

  const onFindClick = () => getJournalList(selectRecord);

  const gridSize = { xs: 12, sm: 6 };
  const gridSize2 = { xs: 12, sm: 5 };

  const getNumberRow = (indx) => {
    return (curPage - 1) * countRecord + indx + 1;
  };

  return (
    <CusPageCardContainer
      title={`${t('User log')} ${user.login || ''}`}
      footer={null}
      backInfo={'auto'}>
      <Stack display={'flex'} direction={'column'} gap={1}>
        <Stack gap={0.5}>
          <Grid container spacing={1}>
            <Grid size={gridSize}>
              <CusDatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                time={'00:00:00'}
              />
            </Grid>
            <Grid size={gridSize}>
              <CusDatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                time={'23:59:59'}
                errorText={
                  startDate && endDate && startDate > endDate
                    ? t('End date must be later than start date')
                    : ''
                }
              />
            </Grid>
            <Grid size={gridSize2}>
              <CusLabelSelect
                label={'Events'}
                value={selectEvents}
                options={eventsOptions}
                onChange={setSelectEvents}
                multi
                colorsOptions={colorsOptions}
              />
            </Grid>
            <Grid size={gridSize2}>
              <CusLabelSelect
                label={'Records per page'}
                value={selectRecord}
                options={postOptions}
                onChange={setSelectRecord}
              />
            </Grid>
            <Grid size={'grow'} alignSelf={'end'}>
              <CusButton fullWidth onClick={onFindClick} text="Find" variant="contained" />
            </Grid>
          </Grid>
        </Stack>
        <ShowContainer condition={allLogs.length}>
          <LogsTable
            logs={logs}
            eventFiltered={eventFiltered}
            setEventFiltered={setEventFiltered}
            getColorRow={getColorRow}
            getNumberRow={getNumberRow}
          />

          <Stack
            gap={1}
            justifyContent={'space-between'}
            direction={'row'}
            alignItems={'center'}
            mt={1}>
            <Stack flex={1}>
              <Pagination
                count={count}
                color="primary"
                page={curPage}
                onChange={(_, value) => setCurPage(value)}
              />
            </Stack>
          </Stack>
        </ShowContainer>
      </Stack>
    </CusPageCardContainer>
  );
}
